import {useCallback, useEffect, useState} from 'react';

import {vidyoProviderLogs} from '@/utils/general';

type PermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

type MediaPermissions = {
    camera: PermissionState;
    microphone: PermissionState;
};

export type {MediaPermissions, PermissionState};

const queryPermission = async (name: PermissionName): Promise<PermissionStatus | null> => {
    try {
        return await navigator.permissions.query({name});
    } catch {
        return null;
    }
};

export const useMediaPermissions = () => {
    const [permissions, setPermissions] = useState<MediaPermissions>({
        camera: 'unknown',
        microphone: 'unknown',
    });

    const refresh = useCallback(async () => {
        const [camStatus, micStatus] = await Promise.all([
            queryPermission('camera' as PermissionName),
            queryPermission('microphone' as PermissionName),
        ]);

        const next: MediaPermissions = {
            camera: (camStatus?.state) ?? 'unknown',
            microphone: (micStatus?.state) ?? 'unknown',
        };

        if (next.camera === 'denied' || next.microphone === 'denied') {
            vidyoProviderLogs({
                message: `Media permissions denied – camera: ${next.camera}, microphone: ${next.microphone}`,
                eventType: 'device',
                status: 'error',
            });
        }

        setPermissions(next);

        // Listen for permission changes so the UI updates in real-time
        if (camStatus) {
            camStatus.onchange = () => {
                const state = camStatus.state as PermissionState;
                if (state === 'denied') {
                    vidyoProviderLogs({
                        message: 'Camera permission changed to: denied',
                        eventType: 'device',
                        status: 'error',
                    });
                }
                setPermissions((prev) => ({...prev, camera: state}));
            };
        }
        if (micStatus) {
            micStatus.onchange = () => {
                const state = micStatus.state as PermissionState;
                if (state === 'denied') {
                    vidyoProviderLogs({
                        message: 'Microphone permission changed to: denied',
                        eventType: 'device',
                        status: 'error',
                    });
                }
                setPermissions((prev) => ({...prev, microphone: state}));
            };
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const requestPermissions = useCallback(
        async (opts: {video?: boolean; audio?: boolean}) => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia(opts);
                // Stop the tracks immediately – we only needed the permission grant
                stream.getTracks().forEach((t) => t.stop());
                void refresh();
                return true;
            } catch (err) {
                vidyoProviderLogs({
                    message: 'getUserMedia permission request rejected',
                    eventType: 'device',
                    status: 'error',
                    error: err,
                });
                void refresh();
                return false;
            }
        },
        [refresh],
    );

    return {permissions, refresh, requestPermissions};
};
